import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { uniqueId, invariant, ErrorConstant, isFunction, pascalCase, kebabCase, isElementDomNode, isNodeOfType, isString, isDomNode, isPlainObject, entries, SELECTED_NODE_CLASS_NAME, extension, PlainExtension, object, isNodeExtension } from '@remirror/core';
import _classPrivateFieldSet from '@babel/runtime/helpers/esm/classPrivateFieldSet';
import _classPrivateFieldGet from '@babel/runtime/helpers/esm/classPrivateFieldGet';
import { DOMSerializer } from '@remirror/pm/model';
import { NodeSelection } from '@remirror/pm/state';
import { jsx, Fragment } from 'react/jsx-runtime';
import { createNanoEvents } from 'nanoevents';
import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

function _checkPrivateRedeclaration(obj, privateCollection) {
  if (privateCollection.has(obj)) {
    throw new TypeError("Cannot initialize the same private elements twice on an object");
  }
}

function _classPrivateFieldInitSpec(obj, privateMap, value) {
  _checkPrivateRedeclaration(obj, privateMap);
  privateMap.set(obj, value);
}

var _events = /*#__PURE__*/new WeakMap();

/**
 * The node view portal container keeps track of all the portals which have been
 * added by react to render the node views in the editor.
 */
class PortalContainer {
  constructor() {
    _defineProperty(this, "portals", new Map());

    _classPrivateFieldInitSpec(this, _events, {
      writable: true,
      value: createNanoEvents()
    });

    _defineProperty(this, "on", callback => {
      return _classPrivateFieldGet(this, _events).on('update', callback);
    });

    _defineProperty(this, "once", callback => {
      var unbind = _classPrivateFieldGet(this, _events).on('update', portals => {
        unbind();
        callback(portals);
      });

      return unbind;
    });
  }

  /**
   * Trigger an update in all subscribers.
   */
  update() {
    _classPrivateFieldGet(this, _events).emit('update', this.portals);
  }
  /**
   * Responsible for registering a new portal by rendering the react element
   * into the provided container.
   */


  render(_ref) {
    var _portal$key;

    var Component = _ref.Component,
        container = _ref.container;
    var portal = this.portals.get(container);
    this.portals.set(container, {
      Component,
      key: (_portal$key = portal === null || portal === void 0 ? void 0 : portal.key) !== null && _portal$key !== void 0 ? _portal$key : uniqueId()
    });
    this.update();
  }
  /**
   * Force an update in all the portals by setting new keys for every portal.
   *
   * Delete all orphaned containers (deleted from the DOM). This is useful for
   * Decoration where there is no destroy method.
   */


  forceUpdate() {
    var _iterator = _createForOfIteratorHelper(this.portals),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _step$value = _slicedToArray(_step.value, 2),
            container = _step$value[0],
            Component = _step$value[1].Component;

        this.portals.set(container, {
          Component,
          key: uniqueId()
        });
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }
  /**
   * Deletes the portal within the container.
   */


  remove(container) {
    // Remove the portal which was being wrapped in the provided container.
    this.portals.delete(container); // Trigger an update

    this.update();
  }

}

/**
 * The component that places all the portals into the DOM.
 *
 * Portals can currently be created by a [[`ReactNodeView`]] and coming soon
 * both the [[`ReactMarkView`]] and [[`ReactDecoration`]].
 */
var RemirrorPortals = props => {
  var portals = props.portals;
  return /*#__PURE__*/jsx(Fragment, {
    children: portals.map(_ref => {
      var _ref2 = _slicedToArray(_ref, 2),
          container = _ref2[0],
          _ref2$ = _ref2[1],
          Component = _ref2$.Component,
          key = _ref2$.key;

      return /*#__PURE__*/createPortal( /*#__PURE__*/jsx(Component, {}), container, key);
    })
  });
};

/**
 * A hook which subscribes to updates from the portal container.
 *
 * This is should used in the `ReactEditor` component and the value should be
 * passed through to the `RemirrorPortals` component.
 */
function usePortals(portalContainer) {
  var _useState = useState(() => [...portalContainer.portals.entries()]),
      _useState2 = _slicedToArray(_useState, 2),
      portals = _useState2[0],
      setPortals = _useState2[1]; // Dispose of all portals.


  useEffect(() => {
    // Auto disposed when the component un-mounts.
    return portalContainer.on(portalMap => {
      setPortals([...portalMap.entries()]);
    });
  }, [portalContainer]);
  return useMemo(() => portals, [portals]);
}

var _node = /*#__PURE__*/new WeakMap();

var _decorations = /*#__PURE__*/new WeakMap();

var _view = /*#__PURE__*/new WeakMap();

var _portalContainer = /*#__PURE__*/new WeakMap();

var _Component = /*#__PURE__*/new WeakMap();

var _getPosition = /*#__PURE__*/new WeakMap();

var _options = /*#__PURE__*/new WeakMap();

var _selected = /*#__PURE__*/new WeakMap();

var _contentDOM = /*#__PURE__*/new WeakMap();

var _contentDOMWrapper = /*#__PURE__*/new WeakMap();

var _dom = /*#__PURE__*/new WeakMap();

var _forwardRef = /*#__PURE__*/new WeakMap();

/**
 * This is the node view rapper that makes
 */
class ReactNodeView {
  /**
   * A shorthand method for creating the `ReactNodeView`.
   */
  static create(props) {
    var portalContainer = props.portalContainer,
        ReactComponent = props.ReactComponent,
        options = props.options;
    return (node, view, getPosition) => new ReactNodeView({
      options,
      node,
      view,
      getPosition,
      portalContainer,
      ReactComponent
    });
  }
  /**
   * The `ProsemirrorNode` that this nodeView is responsible for rendering.
   */


  /**
   * Whether or not the node is currently selected.
   */
  get selected() {
    return _classPrivateFieldGet(this, _selected);
  }

  /**
   * The DOM node that should hold the node's content.
   *
   * This is only meaningful if the NodeView is not a leaf type and it can
   * accept content. When these criteria are met, `ProseMirror` will take care of
   * rendering the node's children into it.
   *
   * In order to make use of this in a
   */
  get contentDOM() {
    return _classPrivateFieldGet(this, _contentDOM);
  }

  /**
   * Provides readonly access to the dom element. The dom is automatically for
   * react components.
   */
  get dom() {
    return _classPrivateFieldGet(this, _dom);
  }
  /**
   * Create the node view for a react component and render it into the dom.
   */


  constructor(_ref) {
    var _this$createContentDo;

    var getPosition = _ref.getPosition,
        _node2 = _ref.node,
        portalContainer = _ref.portalContainer,
        view = _ref.view,
        _ReactComponent = _ref.ReactComponent,
        options = _ref.options;

    _classPrivateFieldInitSpec(this, _node, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldInitSpec(this, _decorations, {
      writable: true,
      value: []
    });

    _classPrivateFieldInitSpec(this, _view, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldInitSpec(this, _portalContainer, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldInitSpec(this, _Component, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldInitSpec(this, _getPosition, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldInitSpec(this, _options, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldInitSpec(this, _selected, {
      writable: true,
      value: false
    });

    _classPrivateFieldInitSpec(this, _contentDOM, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldInitSpec(this, _contentDOMWrapper, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldInitSpec(this, _dom, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldInitSpec(this, _forwardRef, {
      writable: true,
      value: node => {
        if (!node) {
          return;
        }

        !_classPrivateFieldGet(this, _contentDOMWrapper) ? process.env.NODE_ENV !== "production" ? invariant(false, {
          code: ErrorConstant.REACT_NODE_VIEW,
          message: "You have applied a ref to a node view provided for '".concat(_classPrivateFieldGet(this, _node).type.name, "' which doesn't support content.")
        }) : invariant(false) : void 0;
        node.append(_classPrivateFieldGet(this, _contentDOMWrapper));
      }
    });

    _defineProperty(this, "Component", () => {
      var ReactComponent = _classPrivateFieldGet(this, _Component);

      !ReactComponent ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.REACT_NODE_VIEW,
        message: "The custom react node view provided for ".concat(_classPrivateFieldGet(this, _node).type.name, " doesn't have a valid ReactComponent")
      }) : invariant(false) : void 0;
      return /*#__PURE__*/jsx(ReactComponent, {
        environment: "dom",
        updateAttributes: this.updateAttributes,
        selected: this.selected,
        view: _classPrivateFieldGet(this, _view),
        getPosition: _classPrivateFieldGet(this, _getPosition),
        node: _classPrivateFieldGet(this, _node),
        forwardRef: _classPrivateFieldGet(this, _forwardRef),
        decorations: _classPrivateFieldGet(this, _decorations)
      });
    });

    _defineProperty(this, "updateAttributes", attrs => {
      if (!_classPrivateFieldGet(this, _view).editable) {
        return;
      }

      var tr = _classPrivateFieldGet(this, _view).state.tr.setNodeMarkup(_classPrivateFieldGet(this, _getPosition).call(this), undefined, _objectSpread(_objectSpread({}, _classPrivateFieldGet(this, _node).attrs), attrs));

      _classPrivateFieldGet(this, _view).dispatch(tr);
    });

    !isFunction(getPosition) ? process.env.NODE_ENV !== "production" ? invariant(false, {
      message: 'You are attempting to use a node view for a mark type. This is not supported yet. Please check your configuration.'
    }) : invariant(false) : void 0;

    _classPrivateFieldSet(this, _node, _node2);

    _classPrivateFieldSet(this, _view, view);

    _classPrivateFieldSet(this, _portalContainer, portalContainer);

    _classPrivateFieldSet(this, _Component, _ReactComponent);

    _classPrivateFieldSet(this, _getPosition, getPosition);

    _classPrivateFieldSet(this, _options, options);

    _classPrivateFieldSet(this, _dom, this.createDom());

    var _ref2 = (_this$createContentDo = this.createContentDom()) !== null && _this$createContentDo !== void 0 ? _this$createContentDo : {},
        contentDOM = _ref2.contentDOM,
        wrapper = _ref2.wrapper;

    _classPrivateFieldSet(this, _contentDOM, contentDOM !== null && contentDOM !== void 0 ? contentDOM : undefined);

    _classPrivateFieldSet(this, _contentDOMWrapper, wrapper);

    if (_classPrivateFieldGet(this, _contentDOMWrapper)) {
      _classPrivateFieldGet(this, _dom).append(_classPrivateFieldGet(this, _contentDOMWrapper));
    }

    this.setDomAttributes(_classPrivateFieldGet(this, _node), _classPrivateFieldGet(this, _dom));
    this.Component.displayName = pascalCase("".concat(_classPrivateFieldGet(this, _node).type.name, "NodeView"));
    this.renderComponent();
  }
  /**
   * Render the react component into the dom.
   */


  renderComponent() {
    _classPrivateFieldGet(this, _portalContainer).render({
      Component: this.Component,
      container: _classPrivateFieldGet(this, _dom)
    });
  }
  /**
   * Create the dom element which will hold the react component.
   */


  createDom() {
    var _classPrivateFieldGet2 = _classPrivateFieldGet(this, _options),
        defaultBlockNode = _classPrivateFieldGet2.defaultBlockNode,
        defaultInlineNode = _classPrivateFieldGet2.defaultInlineNode;

    var element = _classPrivateFieldGet(this, _node).isInline ? document.createElement(defaultInlineNode) : document.createElement(defaultBlockNode); // ProseMirror breaks down when it encounters multiple nested empty
    // elements. This class prevents this from happening.

    element.classList.add("".concat(kebabCase(_classPrivateFieldGet(this, _node).type.name), "-node-view-wrapper"));
    return element;
  }
  /**
   * The element that will contain the content for this element.
   */


  createContentDom() {
    var _classPrivateFieldGet3, _classPrivateFieldGet4;

    if (_classPrivateFieldGet(this, _node).isLeaf) {
      return;
    }

    var domSpec = (_classPrivateFieldGet3 = (_classPrivateFieldGet4 = _classPrivateFieldGet(this, _node).type.spec).toDOM) === null || _classPrivateFieldGet3 === void 0 ? void 0 : _classPrivateFieldGet3.call(_classPrivateFieldGet4, _classPrivateFieldGet(this, _node)); // Only allow content if a domSpec exists which is used to render the content.

    if (!domSpec) {
      return;
    } // Let `ProseMirror` interpret the domSpec returned by `toDOM` to provide
    // the dom and `contentDOM`.


    var _DOMSerializer$render = DOMSerializer.renderSpec(document, domSpec),
        contentDOM = _DOMSerializer$render.contentDOM,
        dom = _DOMSerializer$render.dom; // The content dom needs a wrapper node in react since the dom element which
    // it renders inside isn't immediately mounted.


    var wrapper;

    if (!isElementDomNode(dom)) {
      return;
    } // Default to setting the wrapper to a different element.


    wrapper = dom;

    if (dom === contentDOM) {
      wrapper = document.createElement('span');
      wrapper.classList.add("".concat(kebabCase(_classPrivateFieldGet(this, _node).type.name), "-node-view-content-wrapper"));
      wrapper.append(contentDOM);
    }

    if (isElementDomNode(contentDOM)) ;

    return {
      wrapper,
      contentDOM
    };
  }
  /**
   * Adds a ref to the component that has been provided and can be used to set
   * it as the content container. However it is advisable to either not use
   * ReactNodeViews for nodes with content or to take control of rendering the
   * content within the component..
   */


  /**
   * This is called whenever the node is called.
   */
  update(node, decorations) {
    if (!isNodeOfType({
      types: _classPrivateFieldGet(this, _node).type,
      node
    })) {
      return false;
    }

    if (_classPrivateFieldGet(this, _node) === node && _classPrivateFieldGet(this, _decorations) === decorations) {
      return true;
    }

    if (!_classPrivateFieldGet(this, _node).sameMarkup(node)) {
      this.setDomAttributes(node, _classPrivateFieldGet(this, _dom));
    }

    _classPrivateFieldSet(this, _node, node);

    _classPrivateFieldSet(this, _decorations, decorations);

    this.renderComponent();
    return true;
  }
  /**
   * Copies the attributes from a ProseMirror Node to the parent DOM node.
   *
   * @param node The Prosemirror Node from which to source the attributes
   */


  setDomAttributes(node, element) {
    var toDOM = _classPrivateFieldGet(this, _node).type.spec.toDOM;

    var attributes = node.attrs;

    if (toDOM) {
      var domSpec = toDOM(node);

      if (isString(domSpec) || isDomNodeOutputSpec(domSpec)) {
        return;
      }

      if (isPlainObject(domSpec[1])) {
        attributes = domSpec[1];
      }
    }

    var _iterator = _createForOfIteratorHelper(entries(attributes)),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _step$value = _slicedToArray(_step.value, 2),
            attribute = _step$value[0],
            value = _step$value[1];

        element.setAttribute(attribute, value);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }
  /**
   * Marks the node as being selected.
   */


  selectNode() {
    _classPrivateFieldSet(this, _selected, true);

    if (_classPrivateFieldGet(this, _dom)) {
      _classPrivateFieldGet(this, _dom).classList.add(SELECTED_NODE_CLASS_NAME);
    }

    this.renderComponent();
  }
  /**
   * Remove the selected node markings from this component.
   */


  deselectNode() {
    _classPrivateFieldSet(this, _selected, false);

    if (_classPrivateFieldGet(this, _dom)) {
      _classPrivateFieldGet(this, _dom).classList.remove(SELECTED_NODE_CLASS_NAME);
    }

    this.renderComponent();
  }
  /**
   * This is called whenever the node is being destroyed.
   */


  destroy() {
    _classPrivateFieldGet(this, _portalContainer).remove(_classPrivateFieldGet(this, _dom));
  }
  /**
   * The handler which decides when mutations should be ignored.
   */


  ignoreMutation(mutation) {
    if (mutation.type === 'selection') {
      // If a node type is unselectable, then ignore all selection mutations.
      return !_classPrivateFieldGet(this, _node).type.spec.selectable;
    }

    if (!_classPrivateFieldGet(this, _contentDOMWrapper)) {
      return true;
    }

    return !_classPrivateFieldGet(this, _contentDOMWrapper).contains(mutation.target);
  }

  stopEvent(event) {
    var _this$contentDOM;

    if (!_classPrivateFieldGet(this, _dom)) {
      return false;
    }

    if (isFunction(_classPrivateFieldGet(this, _options).stopEvent)) {
      return _classPrivateFieldGet(this, _options).stopEvent({
        event
      });
    }

    var target = event.target;
    var isInElement = _classPrivateFieldGet(this, _dom).contains(target) && !((_this$contentDOM = this.contentDOM) !== null && _this$contentDOM !== void 0 && _this$contentDOM.contains(target)); // any event from child nodes should be handled by ProseMirror

    if (!isInElement) {
      return false;
    }

    var isDropEvent = event.type === 'drop';
    var isInput = ['INPUT', 'BUTTON', 'SELECT', 'TEXTAREA'].includes(target.tagName) || target.isContentEditable; // any input event within node views should be ignored by ProseMirror

    if (isInput && !isDropEvent) {
      return true;
    }

    var isDraggable = !!_classPrivateFieldGet(this, _node).type.spec.draggable;
    var isSelectable = NodeSelection.isSelectable(_classPrivateFieldGet(this, _node));
    var isCopyEvent = event.type === 'copy';
    var isPasteEvent = event.type === 'paste';
    var isCutEvent = event.type === 'cut';
    var isClickEvent = event.type === 'mousedown';
    var isDragEvent = event.type.startsWith('drag'); // ProseMirror tries to drag selectable nodes
    // even if `draggable` is set to `false`
    // this fix prevents that

    if (!isDraggable && isSelectable && isDragEvent) {
      event.preventDefault();
    }

    if (isDraggable && isDragEvent) {
      event.preventDefault();
      return false;
    } // these events are handled by prosemirror


    if (isDropEvent || isCopyEvent || isPasteEvent || isCutEvent || isClickEvent && isSelectable) {
      return false;
    }

    return true;
  }

}

function isDomNodeOutputSpec(value) {
  return isDomNode(value) || isPlainObject(value) && isDomNode(value.dom);
}

var _dec, _class;
/**
 * The extension transforms the `ReactComponent` property on extensions into the
 * following:
 *
 * - a valid `NodeView` wrapped dom element
 * - a valid `SSR` component.
 *
 * Currently this only support nodes. Support will be added for marks later.
 *
 * @remarks
 *
 * When creating a NodeView using the component property the `toDOM` method
 * returned by the `createNodeSpec` methods needs to be in the following format.
 *
 * - `string` - e.g. `div`. This will be used as the wrapper tag name. .
 * - `[string, 0]` - The wrapper tag name and a `0` indicating that this will be
 *   accepting content.
 * - `[string, object, 0?]` -The wrapper tag name, an object of the attributes
 *   that should be applied to the wrapper tag and a 0 when you want the react
 *   component to have content inserted into it.
 *
 * Unfortunately `React Components` currently require a wrapping tag element
 * when being used in the DOM. See the following for the reasons.
 *
 * ### Caveats
 *
 * It's not possible to create a node view without nested dom element in `react`
 * due to this issue https://github.com/facebook/react/issues/12227. It's
 * unlikely that this limitation will be changed any time soon
 * https://github.com/ProseMirror/prosemirror/issues/803
 *
 * NodeViews have a `dom` node which is used as the main wrapper element. For
 * paragraphs this would be the `p` tag and for text this is a `TEXT` node.
 * NodeView's  also have a `contentDOM` property which is where any content from
 * ProseMirror is injected.
 *
 * The difficulty in integration is that the dom node and the content dom node
 * of the `NodeView` are consumed synchronously by ProseMirror. However, react
 * requires a ref to capture the dom node which corresponds to the mounted
 * component. This is done asynchronously. As a result it's not possible to
 * provide the `dom` node or `contentDOM` to ProseMirror while using react.
 *
 * The only way around this is to create both the top level `dom` element and
 * the `contentDOM` element manually in the NodeView and provide a `forwardRef`
 * prop to the component. This prop must be attached to the part of the tree
 * where content should be rendered to. Once the React ref is available the
 * `forwardRef` prop appends the `contentDOM` to the element where `forwardRef`
 * was attached.
 */

var ReactComponentExtension = (_dec = extension({
  defaultOptions: {
    defaultBlockNode: 'div',
    defaultInlineNode: 'span',
    defaultContentNode: 'span',
    defaultEnvironment: 'both',
    nodeViewComponents: {},
    stopEvent: null
  },
  staticKeys: ['defaultBlockNode', 'defaultInlineNode', 'defaultContentNode', 'defaultEnvironment']
}), _dec(_class = class ReactComponentExtension extends PlainExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "portalContainer", new PortalContainer());
  }

  get name() {
    return 'reactComponent';
  }
  /**
   * Add the portal container to the manager store. This can be used by the
   * `<Remirror />` component to manage portals for node content.
   */


  onCreate() {
    this.store.setStoreKey('portalContainer', this.portalContainer);
  }
  /**
   * Create the node views from the custom components provided.
   */


  createNodeViews() {
    var _this$store$managerSe;

    var nodeViews = object();
    var managerComponents = (_this$store$managerSe = this.store.managerSettings.nodeViewComponents) !== null && _this$store$managerSe !== void 0 ? _this$store$managerSe : {}; // Loop through the extension to pick out the ones with custom components.

    var _iterator = _createForOfIteratorHelper(this.store.extensions),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _extension = _step.value;

        if (!_extension.ReactComponent || !isNodeExtension(_extension) || _extension.reactComponentEnvironment === 'ssr') {
          continue;
        }

        nodeViews[_extension.name] = ReactNodeView.create({
          options: this.options,
          ReactComponent: _extension.ReactComponent,
          portalContainer: this.portalContainer
        });
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    var namedComponents = entries(_objectSpread(_objectSpread({}, this.options.nodeViewComponents), managerComponents)); // Add the custom react components from the extension settings and manager setting.

    var _iterator2 = _createForOfIteratorHelper(namedComponents),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var _step2$value = _slicedToArray(_step2.value, 2),
            name = _step2$value[0],
            ReactComponent = _step2$value[1];

        nodeViews[name] = ReactNodeView.create({
          options: this.options,
          ReactComponent,
          portalContainer: this.portalContainer
        });
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }

    return nodeViews;
  }

}) || _class);

export { PortalContainer, ReactComponentExtension, RemirrorPortals, usePortals };
